import { api } from '@/lib/api';
import { mutationOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mutationKeys } from '../mutationKeys';

export interface CreateInvitationRequest {
  email: string;
  name: string;
  surname: string;
  role: 'Customer' | 'Doctor';
  clinic_id?: string;
  phone?: string;
}

export interface Inviter {
  id: number;
  name: string;
  surname: string;
}

export interface InvitationUser {
  id: number;
  name: string;
  surname: string;
  email: string;
}

export interface InvitationResponse {
  uuid: string;
  user_id?: number;
  user?: InvitationUser;
  email: string;
  name: string;
  surname: string;
  role: string;
  restricted: boolean;
  accepted_at: string | null;
  created_at: string;
  invitation_link: string;
  inviter: Inviter;
}

export const createInvitationMutation = () => {
  return mutationOptions({
    mutationKey: mutationKeys.invitations.create(),
    mutationFn: async (values: CreateInvitationRequest) => {
      return await api
        .post('invitations', { json: values })
        .json<InvitationResponse>();
    },
    onSuccess: () => {
      toast.success('Invitation sent successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send invitation');
    },
  });
};

export const deleteInvitationMutation = () => {
  return mutationOptions({
    mutationKey: mutationKeys.invitations.delete(''),
    mutationFn: async (uuid: string) => {
      return await api.delete(`admin/invitations/${uuid}`).json();
    },
    onSuccess: () => {
      toast.success('Invitation deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete invitation');
    },
  });
};

export const deleteInvitationAsDoctorMutation = () => {
  return mutationOptions({
    mutationKey: [...mutationKeys.invitations.delete(''), 'doctor'],
    mutationFn: async (uuid: string) => {
      return await api.delete(`invitations/${uuid}`).json();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete invitation');
    },
  });
};

export interface AcceptInvitationRequest {
  password: string;
  phone?: string;
}

export const acceptInvitationMutation = () => {
  return mutationOptions({
    mutationKey: [...mutationKeys.invitations.create(), 'accept'],
    mutationFn: async ({
      uuid,
      body,
    }: {
      uuid: string;
      body?: AcceptInvitationRequest;
    }) => {
      return await api
        .post(`invitations/${uuid}/accept`, body ? { json: body } : undefined)
        .json<{ data: InvitationResponse }>();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to accept invitation');
    },
  });
};

export const sendInvitationReminderMutation = () => {
  return mutationOptions({
    mutationKey: mutationKeys.invitations.sendReminder(''),
    mutationFn: async (uuid: string) => {
      return await api.post(`admin/invitations/${uuid}/reminder`).json();
    },
    onSuccess: () => {
      toast.success('Reminder sent successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send reminder');
    },
  });
};
